import { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Recommendation } from '@/types/types'
import { RecommendationItem } from './recommendation-item'

const Container = styled.div`
  padding: 20px;

  @media (max-width: 800px) {
    padding-bottom: 85px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  h5 {
    text-align: left;
    margin: 0;
  }
`

const CompletedLabel = styled.div`
  font-weight: 700;
`

const EmptyState = styled.div`
  display: grid;
  color: #082842;
  height: 100%;
  text-align: left;

  span {
    margin: 10px 0;
  }
`

const RecommendationsList = styled.div`
  @media (max-width: 800px) {
    div {
      scroll-snap-align: start;
    }
  }
`

export interface RecommendationsProps {
  recommendations: Recommendation[]
  flows: object
  mainColor?: string
}

export const Recommendations = ({
  recommendations,
  flows,
  mainColor,
}: RecommendationsProps) => {
  const uncompleteRecommendation = useMemo(() => {
    return recommendations
      .filter(({ completed }) => !completed)
      .sort((a, b) => a.priority - b.priority)
      .reverse()
  }, [recommendations])

  const completeRecommendation = useMemo(
    () => recommendations.filter(({ completed }) => completed),
    [recommendations],
  )

  useEffect(() => {
    if (uncompleteRecommendation.length > 0) {
      document.title = `Bot (${uncompleteRecommendation.length})`
    } else {
      document.title = 'Bot'
    }
  }, [uncompleteRecommendation.length])

  const progressLabel =
    completeRecommendation.length === recommendations.length
      ? ''
      : `${completeRecommendation.length} out of ${recommendations.length} complete`

  return (
    <Container>
      <Header className="side-by-side-flex">
        <h5>Tasks</h5>
        <p>{progressLabel}</p>
      </Header>
      {recommendations.length > 0 && (
        <RecommendationsList>
          {uncompleteRecommendation.map((rec, index) => (
            <RecommendationItem
              key={`uncomplete${index}`}
              rec={rec}
              mainColor={mainColor}
            />
          ))}
          <CompletedLabel>
            {completeRecommendation.length > 0 && <p>Completed</p>}
          </CompletedLabel>
          {completeRecommendation.map((rec, index) => (
            <RecommendationItem
              key={`complete${index}`}
              rec={rec}
              mainColor={mainColor}
            />
          ))}
        </RecommendationsList>
      )}
      {Object.keys(flows).length === 0 && (
        <EmptyState>
          <h4>We'll be ready in a moment.</h4>
          <h4>Fetching current Progress & Tasks...</h4>
        </EmptyState>
      )}
      {recommendations.length === 0 && Object.keys(flows).length > 0 && (
        <EmptyState>
          <span>
            No additional tasks! We'll keep you in the loop if there's anything we need
            you to do.
          </span>
        </EmptyState>
      )}
    </Container>
  )
}
